/* eslint-disable no-loop-func */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Report, Page } from 'cccisd-laravel-report';
import _uniq from 'lodash/uniq';
import Loader from 'cccisd-loader';
import Table from 'cccisd-table';
import Header from './Header.js';
import questionsQuery from './questions.graphql';
import answersQuery from './answers.graphql';
import classnames from 'classnames';
import { client } from 'cccisd-apollo';
import style from './style.css';

const ItemDetails = props => {
    const { pages, deploymentIds, pawnDeploymentIds, student } = props;
    const [surveyAssessments, setSurveyAssessments] = useState(null);

    useEffect(() => {
        (async () => {
            const surveyIds = _uniq(pages.map(page => page.surveyId));
            const pawnIds = pages.map(page => page.pawnId);

            const res = await client.query({
                query: questionsQuery,
                variables: { surveyId: surveyIds },
                fetchPolicy: 'network-only',
            });

            const surveys = res.data.metrics.variableList.reduce((obj, item) => {
                obj[item.surveyId] ||= {
                    surveyId: item.surveyId,
                    surveyLabel: item.survey.label,
                    variableIds: [],
                    languages: {},
                };
                obj[item.surveyId].languages[item.key.flow.language] ||= { questions: [] };
                obj[item.surveyId].languages[item.key.flow.language].questions.push({
                    variableId: item.variableId,
                    type: item.type,
                    question: item.key.content.title,
                    enum: item.key.content.enum,
                    enumDisplayText: item.key.content.enumDisplayText,
                });
                obj[item.surveyId].variableIds.push(item.variableId);

                return obj;
            }, {});

            const assessments = [];
            for (const survey of Object.values(surveys)) {
                const res1 = await client.query({
                    query: answersQuery,
                    variables: {
                        deploymentId: deploymentIds,
                        pawnId: pawnIds,
                        surveyId: [survey.surveyId],
                        variableId: survey.variableIds,
                    },
                    fetchPolicy: 'network-only',
                });

                assessments.push(
                    ...res1.data.roles.anyRoleList
                        .reduce((arr, item) => {
                            arr.push(...item.assignmentProgressList);
                            return arr;
                        }, [])
                        .filter(
                            item =>
                                Object.keys(item.variablesById).length > 0 &&
                                pawnDeploymentIds[item.pawn.pawn.pawnId]?.includes(item.deploymentId)
                        )
                        .map(item => ({
                            id: item.assignmentProgressId,
                            language: item.language,
                            survey,
                            timepoint: item.completedAt,
                            answers: item.variablesById,
                            reporter:
                                item.pawn.fields.contactName ||
                                `${item.pawn.fields.firstName} ${item.pawn.fields.lastName}`,
                        }))
                );
            }

            setSurveyAssessments(assessments);
        })();
    }, [pages]);

    if (!surveyAssessments) {
        return <Loader loading />;
    }

    if (surveyAssessments.length === 0) {
        return <div className="text-center">No data to show</div>;
    }

    const columns = [
        { name: 'question', label: 'Question', setStyle: () => ({ width: '50%' }) },
        {
            name: 'answer',
            label: 'Answer',
            setStyle: () => ({ width: '50%' }),
            render: (value, row) => {
                if (!row.enumDisplayText) {
                    return row.answer;
                }
                return (
                    <div>
                        {row.enumDisplayText.map(text => (
                            <div
                                key={text}
                                className={classnames('ps-2 pe-2 rounded', text === row.answer && style.selectedAnswer)}
                            >
                                {text}
                            </div>
                        ))}
                    </div>
                );
            },
        },
    ];

    return (
        <Report>
            {surveyAssessments.map(assessment => {
                const { survey, language } = assessment;
                const data = survey.languages[language].questions.map(question => ({
                    ...question,
                    answer: assessment.answers[question.variableId],
                }));

                return (
                    <Page key={assessment.id}>
                        <Header student={student} />
                        <h3 className="mb-2">{survey.surveyLabel}</h3>
                        <div className="mb-4">
                            <div>
                                <b>Reporter:</b> {assessment.reporter}
                            </div>
                            <div>
                                <b>Timepoint:</b> {assessment.timepoint.slice(0, 10)}
                            </div>
                        </div>
                        <Table
                            rowKey="variableId"
                            columns={columns}
                            data={data}
                            perPage={500}
                            showPerPageOptions={false}
                            hideShowingRowsSummary
                        />
                    </Page>
                );
            })}
        </Report>
    );
};

ItemDetails.propTypes = {
    pages: PropTypes.array,
    deploymentIds: PropTypes.array,
    pawnDeploymentIds: PropTypes.object,
    student: PropTypes.object,
};

export default ItemDetails;
